import React, { useState, useEffect } from "react"
import { useSelector } from "react-redux"

import {
    NUMBER,
    SCIENTIFIC,
    FIT, FIT_RATIO,
    MTTF_HOURS, MTTF_YEARS, MTTF_1_YEAR_HOURS,
} from "const"
import { StoreType, UserSettings, LangType } from "client/store"
import { SilFailureRateFormat } from "client/utils"

import FormatInput from "./input"
import FormatView from "./view"


const format = (
    data: string | number | false,
    failureRate: SilFailureRateFormat,
    failureRateDecimal: number,
) => {
    const value = typeof data === "string" ? parseFloat(data) : data
    if (value === undefined) {
        return null
    }
    if (value === null) { // not used
        return null
    }
    if (value === false || Number.isNaN(value) || value < 0) { // incorrect value
        return false
    }

    if (failureRate === SCIENTIFIC) {
        return value.toExponential(failureRateDecimal)
    }
    if (failureRate === NUMBER) {
        return value.toFixed(failureRateDecimal)
    }
    if (failureRate === FIT) {
        return (value * FIT_RATIO).toFixed(failureRateDecimal)
    }
    if (failureRate === MTTF_HOURS) {
        return (1 / value).toFixed(failureRateDecimal)
    }
    if (failureRate === MTTF_YEARS) {
        return (1 / value / MTTF_1_YEAR_HOURS).toFixed(failureRateDecimal)
    }

    return null
}


export const FormatScientificString = (
    data: string | number | false,
    userSettings: UserSettings,
    lang: LangType,
) => {
    const { failureRate, failureRateDecimal } = userSettings.sil
    const result = format(data, failureRate, failureRateDecimal)

    if (result === null) return lang.notUsed
    if (result === false) return lang.insufficientData

    if (failureRate === SCIENTIFIC) {
        return `${result} ${lang.failuresPerHour}`
    }
    if (failureRate === NUMBER) {
        return `${result} ${lang.failuresPerHour}`
    }
    if (failureRate === FIT) {
        return lang.fitX.replace("{0}", result)
    }
    if (failureRate === MTTF_HOURS) {
        return lang.mttfXHours.replace("{0}", result)
    }
    if (failureRate === MTTF_YEARS) {
        return lang.mttfXYears.replace("{0}", result)
    }

    return result
}


interface FormatProps {
    title?: string
    data: string | number | false
    tableView?: boolean
    inputView?: boolean
    validation?: string
    onChange?: (value: string) => void
}

const FormatScientific = ({
    title = null,
    data,
    tableView = false,
    inputView = false,
    validation = null,
    onChange = null,
}: FormatProps) => {
    const silSettings = useSelector((state: StoreType) => state.system.userSettings.sil)
    const lang = useSelector((state: StoreType) => state.system.lang)

    const { failureRate, failureRateDecimal } = silSettings
    const [formatted, setFormatted] = useState<string | false>("")

    useEffect(() => {
        setFormatted(format(data, failureRate, failureRateDecimal))
    }, [data, failureRate, failureRateDecimal])

    const onChangeFormatted: React.ChangeEventHandler<HTMLInputElement> = (e) => setFormatted(e.target.value)
    const onChangeNull = () => {
        onChange(null)
        setFormatted(null)
    }
    const onChangeResult = (value: number) => {
        onChange(value.toExponential())
        setFormatted(format(value, failureRate, failureRateDecimal))
    }


    if (failureRate === SCIENTIFIC) {
        if (!onChange) {
            if (inputView) return <FormatInput title={title} value={formatted || ""} after={lang.failuresPerHour} readOnly />
            return (
                <FormatView
                    title={title}
                    value={formatted}
                    tableView={tableView}
                    after={lang.failuresPerHour}
                />
            )
        }
        return (
            <FormatInput
                title={title}
                value={formatted || ""}
                validation={validation}
                after={lang.failuresPerHour}
                onChange={onChangeFormatted}
                onBlur={(e) => {
                    const parsedValue = parseFloat(e.target.value)
                    if (Number.isNaN(parsedValue)) onChangeNull()
                    else if (parsedValue >= 0) onChangeResult(parsedValue)
                    else onChangeNull()
                }}
            />
        )
    }

    if (failureRate === NUMBER) {
        if (!onChange) {
            if (inputView) return <FormatInput title={title} value={formatted || ""} after={lang.failuresPerHour} readOnly />
            return (
                <FormatView
                    title={title}
                    value={formatted}
                    tableView={tableView}
                    after={lang.failuresPerHour}
                />
            )
        }
        return (
            <FormatInput
                title={title}
                value={formatted || ""}
                validation={validation}
                after={lang.failuresPerHour}
                onChange={onChangeFormatted}
                onBlur={(e) => {
                    const parsedValue = parseFloat(e.target.value)
                    if (Number.isNaN(parsedValue)) onChangeNull()
                    else if (parsedValue >= 0) onChangeResult(parsedValue)
                    else onChangeNull()
                }}
            />
        )
    }

    if (failureRate === FIT) {
        if (!onChange) {
            if (inputView) return <FormatInput title={title} value={formatted || ""} after={lang.fit} readOnly />
            return (
                <FormatView
                    title={title}
                    value={formatted}
                    tableView={tableView}
                    after={lang.fit}
                />
            )
        }
        return (
            <FormatInput
                title={title}
                value={formatted || ""}
                validation={validation}
                after={lang.fit}
                onChange={onChangeFormatted}
                onBlur={(e) => {
                    const parsedValue = parseFloat(e.target.value)
                    if (Number.isNaN(parsedValue)) onChangeNull()
                    else if (parsedValue >= 0) onChangeResult(parsedValue / FIT_RATIO)
                    else onChangeNull()
                }}
            />
        )
    }

    if (failureRate === MTTF_HOURS) {
        if (!onChange) {
            if (inputView) return <FormatInput title={title} value={formatted || ""} after={lang.mttfHours} readOnly />
            return (
                <FormatView
                    title={title}
                    value={formatted}
                    tableView={tableView}
                    after={lang.mttfHours}
                />
            )
        }
        return (
            <FormatInput
                title={title}
                value={formatted || ""}
                validation={validation}
                after={lang.mttfHours}
                onChange={onChangeFormatted}
                onBlur={(e) => {
                    const parsedValue = parseFloat(e.target.value)
                    if (Number.isNaN(parsedValue)) onChangeNull()
                    else if (parsedValue >= 0) onChangeResult(1 / parsedValue)
                    else onChangeNull()
                }}
            />
        )
    }

    if (failureRate === MTTF_YEARS) {
        if (!onChange) {
            if (inputView) return <FormatInput title={title} value={formatted || ""} after={lang.mttfYears} readOnly />
            return (
                <FormatView
                    title={title}
                    value={formatted}
                    tableView={tableView}
                    after={lang.mttfYears}
                />
            )
        }
        return (
            <FormatInput
                title={title}
                value={formatted || ""}
                validation={validation}
                after={lang.mttfYears}
                onChange={onChangeFormatted}
                onBlur={(e) => {
                    const parsedValue = parseFloat(e.target.value)
                    if (Number.isNaN(parsedValue)) onChangeNull()
                    else if (parsedValue >= 0) onChangeResult(1 / (parsedValue * MTTF_1_YEAR_HOURS))
                    else onChangeNull()
                }}
            />
        )
    }

    return null
}

export default FormatScientific
